export const COMMUNITY_CALLOUT_FLAG = "community_callout_explanation_v1";
export const COMMUNITY_CALLOUT_CAMPAIGN = "community_callout_v1";

export type CalloutVariant = "control" | "test";

export type CalloutExplanation = {
  slug: string;
  title: string;
  description: string;
  heading: string;
  introduction: string;
  takeaway: string;
  callout: {
    player: string;
    market: string;
    results: number[];
    average: string;
    line: string;
    cardImage: string;
    historyImage: string;
  };
  examples: {
    id: string;
    tool: string;
    player: string;
    market: string;
    metrics: { label: string; value: string }[];
    verdict: string;
    positive: boolean;
    explanation: string;
    image: { src: string; alt: string; width: number; height: number; aspectRatio: string };
    cta: string;
    destination: string;
  }[];
  source: { label: string; url: string };
};

export const CALLOUT_EXPLANATIONS: CalloutExplanation[] = [
  {
    slug: "last-five-trap",
    title: "Five green boxes don't make a good bet | Upside",
    description: "Why an L5 streak doesn't establish an edge. See actual PrizePicks Optimizer and +EV Sniper examples of checking the price instead.",
    heading: "5 green boxes don't make a good bet.",
    introduction: "The books saw those five games too. Recent form is already an input to today's line.",
    takeaway: "Five games above 12. A 15.2 average. It looks obvious—but those results are public information the books can price in. They don't tell you the chance of going over 12 in this matchup.",
    callout: {
      player: "J.K. Dobbins",
      market: "12 rushing attempts · vs. Kansas City",
      results: [14, 14, 15, 15, 18],
      average: "15.2",
      line: "12",
      cardImage: "/images/learning-center/last-five-trap/dobbins-prizepicks-card.png",
      historyImage: "/images/learning-center/last-five-trap/dobbins-prizepicks-last-five.png",
    },
    examples: [
      {
        id: "optimizer-rj-harvey",
        tool: "PrizePicks Optimizer",
        player: "RJ Harvey",
        market: "Under 2.5 receptions · vs. Kansas City",
        metrics: [{ label: "Estimated hit chance", value: "52.6%" }, { label: "Break-even at −119", value: "54.3%" }],
        verdict: "More likely to hit. Still negative value at this price.",
        positive: false,
        explanation: "The Optimizer shows a 52.6% hit estimate and a −119 equivalent price. That price needs about 54.3% to break even. The estimate falls short—even though it's above 50%. The break-even figure is calculated from the displayed price.",
        image: {
          src: "/images/learning-center/last-five-trap/optimizer-rj-harvey.png",
          alt: "Updated PrizePicks Optimizer: RJ Harvey under 2.5 receptions, minus 119 equivalent price, 52.6% estimated hit chance, with expanded sportsbook comparisons.",
          width: 2552, height: 1400, aspectRatio: "3.65 / 1",
        },
        cta: "Check PrizePicks in the Optimizer",
        destination: "/optimizer/props?sportsbook=prizepicks",
      },
      {
        id: "sniper-naomi-osaka",
        tool: "+EV Sniper",
        player: "Naomi Osaka",
        market: "Under 12.5 total games · vs. Ashlyn Krueger",
        metrics: [{ label: "PrizePicks price", value: "−119" }, { label: "Market fair price", value: "−121" }],
        verdict: "+0.70% EV from the price gap.",
        positive: true,
        explanation: "PrizePicks offered a slightly better price than the market estimate. Sniper showed a small +0.70% edge. That's a price comparison you can check.",
        image: {
          src: "/images/email/sniper-market-fair.jpg",
          alt: "Saved +EV Sniper row: Naomi Osaka under 12.5 total games, PrizePicks minus 119, market fair minus 121, plus 0.70% EV.",
          width: 1400, height: 469, aspectRatio: "1400 / 469",
        },
        cta: "Compare prices in +EV Sniper",
        destination: "/ev-sniper",
      },
    ],
    source: {
      label: "Why Upside skips the Last 5 panel",
      url: "/research/last-5-stats-already-priced-in",
    },
  },
];

export function getCalloutCampaign(search: URLSearchParams) {
  const postId = search.get("utm_content");
  if (
    search.get("utm_source") !== "twitter" ||
    search.get("utm_medium") !== "organic_social" ||
    search.get("utm_campaign") !== COMMUNITY_CALLOUT_CAMPAIGN ||
    search.get("placement") !== "reply" ||
    (postId !== null && !/^\d{15,22}$/.test(postId))
  ) return null;

  return { postId, placement: "reply" as const };
}

export function calloutDestination(path: string, search: URLSearchParams): string {
  const [pathname, existingQuery] = path.split("?");
  const attribution = new URLSearchParams(existingQuery);
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "via", "placement"]) {
    const value = search.get(key);
    if (value) attribution.set(key, value);
  }
  const query = attribution.toString();
  return query ? `${pathname}?${query}` : pathname;
}
